import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  NEW_CLIENT_OPTION_VALUE,
  useQuickAddPipeline as useScreensQuickAddPipeline,
} from '@ooh/screens';
import type { QuickAddOfferPickerOpenFullFormArgs } from '@ooh/screens';

export function useQuickAddPipeline() {
  const navigate = useNavigate();

  const openOffer = React.useCallback(
    (offerId: string) => {
      void navigate({ to: '/offers/$offerId', params: { offerId } });
    },
    [navigate],
  );

  const openFullForm = React.useCallback(
    (args: QuickAddOfferPickerOpenFullFormArgs) => {
      const normalizedIds = args.mediaUnitIds.map((id) => Number(id));
      const clientId =
        args.clientId && args.clientId !== NEW_CLIENT_OPTION_VALUE
          ? Number(args.clientId)
          : undefined;
      void navigate({
        to: '/offers/new',
        search: {
          ...(normalizedIds.length > 0 ? { mediaUnitIds: normalizedIds } : {}),
          ...(clientId != null ? { clientId } : {}),
          ...(args.salespersonId != null ? { salespersonId: args.salespersonId } : {}),
          ...(args.defaultDateFrom ? { defaultDateFrom: args.defaultDateFrom } : {}),
          ...(args.defaultDateTo ? { defaultDateTo: args.defaultDateTo } : {}),
        },
      });
    },
    [navigate],
  );

  /**
   * Auto-navigates to the new draft's workspace after a successful
   * non-calendar `creating`-mode commit (CU-86cbc2vj1). Called by
   * `QuickAddOfferPicker`'s confirm-result handling, not from `confirm()`
   * itself, so the co-creators `SharingDialog` deferral can intercept it
   * before any route change.
   */
  const navigateToNewOffer = React.useCallback(
    (offerId: string) => {
      void navigate({
        to: '/offers/$offerId',
        params: { offerId },
        search: { tab: 'units' },
      });
    },
    [navigate],
  );

  const { confirm } = useScreensQuickAddPipeline({ openOffer });

  return { confirm, openFullForm, navigateToNewOffer };
}
